import React, { useState } from 'react';
import { Card, ConfigProvider, Divider, Flex, Typography } from 'antd';
import PropTypes from 'prop-types';
import Icon from '@ant-design/icons';
import { ReactComponent as sos } from '../../../assets/icons/sosAlert.svg';
import { ReactComponent as Major } from '../../../assets/icons/majorAlert.svg';
import { ReactComponent as Minor } from '../../../assets/icons/minorAlert.svg';
import { ReactComponent as Active } from '../../../assets/icons/ongoing.svg';
import { ReactComponent as ActiveSelected } from '../../../assets/icons/ongoing-white.svg';
import { ReactComponent as InActive } from '../../../assets/icons/ongoing-grey.svg';
import { ReactComponent as InActiveLight } from '../../../assets/icons/ongoing-inactive.svg';
import { ReactComponent as InActiveSelectedLight } from '../../../assets/icons/ongoing-inactive-selected.svg';
import { DARK, VEHICLE_STATUS } from '../../../utilities/constants';
import { useTheme } from '../../../context/ThemeContext';

const { Text } = Typography;

const ANOMALY_CSS_VARS = {
  SOS: 'sos-ticket-new',
  Major: 'major-anomaly-color',
  Minor: 'minor-anomaly-color',
};

const ANOMALY_ICON_COLORS = {
  SOS: {
    dark: { selected: '#FF2124', hovered: '#FF6E6F', default: '#FF2124' },
    light: { selected: '#ED0007', hovered: '#BE0004', default: '#ED0007' },
  },
  Major: {
    dark: { selected: '#EEC100', hovered: '#FFCF00', default: '#EEC100' },
    light: { selected: '#FFCF00', hovered: '#AD8C00', default: '#AD8C00' },
  },
  Minor: {
    dark: { selected: '#54ABA5', hovered: '#66B8B2', default: '#54ABA5' },
    light: { selected: '#18837E', hovered: '#2E908B', default: '#419E98' },
  },
};

const getResolvedColor = (variableName, fallback) => {
  if (typeof window === 'undefined') return fallback;

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  return value || fallback;
};

const STATE_SUFFIXES = {
  selected: '-selected',
  hovered: '-hovered',
  default: '',
};

const getAnomalyIconColor = ({ type, isSelected, isHovered, theme }) => {
  const state = isSelected ? 'selected' : isHovered ? 'hovered' : 'default';
  const themeKey = theme === DARK ? 'dark' : 'light';
  const cssVar = ANOMALY_CSS_VARS[type] || ANOMALY_CSS_VARS.Minor;
  const fallback = (ANOMALY_ICON_COLORS[type] || ANOMALY_ICON_COLORS.Minor)[
    themeKey
  ][state];

  return getResolvedColor(`--${cssVar}${STATE_SUFFIXES[state]}`, fallback);
};

const getTripStatusIcon = (theme, tripStatus, isSelected) => {
  const isActive = tripStatus === VEHICLE_STATUS.Active;
  const isInactive = tripStatus === VEHICLE_STATUS.Inactive;

  if (theme === DARK) {
    if (isActive) return isSelected ? ActiveSelected : Active;
    return isInactive ? InActive : null;
  }

  if (isActive) return isSelected ? Active : ActiveSelected;
  return isSelected && isInactive ? InActiveSelectedLight : InActiveLight;
};

const getStickerColors = (theme, isSelected) => {
  if (theme === DARK) {
    return {
      background: isSelected ? '#000000' : '#0096E8',
      color: isSelected ? '#FFFFFF' : '#000000',
    };
  }

  return {
    background: isSelected ? '#FFFFFF' : '#007BC0',
    color: isSelected ? '#000000' : '#FFFFFF',
  };
};

const AnomlayIcon = ({
  icon,
  type,
  count,
  isSelected,
  isHovered,
  theme,
  iconBackgroundColor,
}) => {
  const iconColor = getAnomalyIconColor({ type, isSelected, isHovered, theme });

  return (
    <Flex gap={4} align="center">
      <Flex
        justify="center"
        align="center"
        className="trip-list-anomaly-icon"
        style={{
          backgroundColor: iconBackgroundColor,
          color: iconColor,
        }}
      >
        <Icon component={icon} className="anomaly-icon" />
      </Flex>
      <Text className="tripitem-alerts">{`${type}: ${count}`}</Text>
    </Flex>
  );
};

AnomlayIcon.propTypes = {
  icon: PropTypes.elementType.isRequired,
  type: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  isHovered: PropTypes.bool,
  theme: PropTypes.string,
  iconBackgroundColor: PropTypes.string,
};

const TripsListCard = ({
  item,
  handleTripClick,
  backgroundColor,
  tripStatus,
  iconBackgroundColor,
  isSelected,
}) => {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const tripStatusIcon = getTripStatusIcon(theme, tripStatus, isSelected);
  const stickerColors = getStickerColors(theme, isSelected);
  const hasStickerId = Boolean(item?.sticker_id);
  const hasAnomalies =
    item?.sosCount !== 0 || item?.majorCount !== 0 || item?.minorCount !== 0;

  return (
    <ConfigProvider
      theme={{
        components: {
          Divider: {
            colorSplit:
              theme === DARK ? '#383B3E' : isSelected ? '#E0E2E5' : '#979EA4',
          },
          Typography: {
            colorText: isSelected && `var(--selected-text-color, #000000)`,
          },
        },
      }}
    >
      <Card
        key={`card-trip-detail-${item?.key}`}
        className={`tripitem-card${isSelected ? ' selected' : ''}`}
        variant="borderless"
        onClick={() => handleTripClick(item)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: backgroundColor,
        }}
      >
        <Flex vertical gap={16}>
          <Flex justify="space-between" gap={32}>
            {hasStickerId ? (
              <Text
                style={{
                  backgroundColor: stickerColors.background,
                  color: stickerColors.color,
                }}
                className="dms-sticker-id"
              >
                {item?.sticker_id}
              </Text>
            ) : (
              <Flex gap={8} wrap>
                <Text>Trip ID</Text>
                <Text strong style={{ whiteSpace: 'nowrap' }}>
                  {item.trip_id}
                </Text>
              </Flex>
            )}
            <Flex justify="flex-end" style={{ flex: 1 }}>
              {tripStatusIcon && (
                <Icon component={tripStatusIcon} className="status-icon" />
              )}
            </Flex>
          </Flex>
          {hasStickerId && (
            <Flex gap={8} wrap>
              <Text>Trip ID</Text>
              <Text strong style={{ whiteSpace: 'nowrap' }}>
                {item.trip_id}
              </Text>
            </Flex>
          )}
          <Text strong>{item.registration}</Text>
          {hasAnomalies && <Divider />}
          <Flex gap={8} wrap>
            {item?.sosCount !== 0 && (
              <AnomlayIcon
                icon={sos}
                type="SOS"
                count={item?.sosCount}
                isSelected={isSelected}
                isHovered={isHovered}
                theme={theme}
                iconBackgroundColor={iconBackgroundColor}
              />
            )}
            {item?.majorCount !== 0 && (
              <AnomlayIcon
                icon={Major}
                type="Major"
                count={item?.majorCount}
                isSelected={isSelected}
                isHovered={isHovered}
                theme={theme}
                iconBackgroundColor={iconBackgroundColor}
              />
            )}
            {item?.minorCount !== 0 && (
              <AnomlayIcon
                icon={Minor}
                type="Minor"
                count={item?.minorCount}
                isSelected={isSelected}
                isHovered={isHovered}
                theme={theme}
                iconBackgroundColor={iconBackgroundColor}
              />
            )}
          </Flex>
        </Flex>
      </Card>
    </ConfigProvider>
  );
};

TripsListCard.propTypes = {
  item: PropTypes.object.isRequired,
  handleTripClick: PropTypes.func.isRequired,
  backgroundColor: PropTypes.string,
  tripStatus: PropTypes.string,
  iconBackgroundColor: PropTypes.string,
  isSelected: PropTypes.bool,
};

export default TripsListCard;
